import { useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Scales, Warning } from '@phosphor-icons/react';

/**
 * Referral dialog that creates a legal claim from an invoice or contract.
 * Props: { invoice_id?, contract_no?, contextLabel?, onDone? }
 */
export default function ReferToLegalButton({ invoice_id, contract_no, contextLabel, onDone, size = 'sm', variant = 'outline' }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [claimType, setClaimType] = useState('financial');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [preview, setPreview] = useState(null);

  const openDialog = async () => {
    setOpen(true);
    // Optional: fetch minimal preview from invoice_id or contract_no for summary
    try {
      if (contract_no) {
        const { data } = await api.get('/invoices', { params: { limit: 2000 } });
        const rel = data.filter(i => i.contract_no === contract_no);
        setPreview({
          count: rel.length,
          total: rel.reduce((s, i) => s + Number(i.invoice_amount || 0), 0),
          paid: rel.reduce((s, i) => s + Number(i.collected_amount || 0), 0),
          remaining: rel.reduce((s, i) => s + Number(i.remaining_amount || 0), 0),
          tenant: rel[0]?.tenant_name,
        });
      } else if (invoice_id) {
        const { data } = await api.get(`/invoices/${invoice_id}`);
        setPreview({
          count: 1, total: data.invoice_amount, paid: data.collected_amount,
          remaining: data.remaining_amount, tenant: data.tenant_name,
        });
      }
    } catch {}
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/legal/refer', {
        contract_no, invoice_id, claim_type: claimType, notes, priority,
      });
      toast.success(`تم إحالة الملف للإدارة القانونية · ${data.claim_no}`);
      setOpen(false);
      setNotes('');
      onDone && onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشلت الإحالة');
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} onClick={openDialog} data-testid="refer-to-legal-btn" className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white">
        <Scales className="ms-1" size={14} weight="duotone" /> إحالة إلى الإدارة القانونية
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scales size={20} className="text-[#1E3A5F]" /> إحالة إلى الإدارة القانونية</DialogTitle>
            <DialogDescription>سيتم إنشاء مطالبة قانونية جديدة برقم مرجعي تلقائي مع نقل كامل بيانات {contextLabel || 'الفاتورة'}.</DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">المستأجر:</span><span className="font-medium">{preview.tenant || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">عدد الفواتير:</span><span className="tabular-nums">{preview.count}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">إجمالي:</span><span className="tabular-nums">{formatMoney(preview.total)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">المسدد:</span><span className="tabular-nums text-emerald-700">{formatMoney(preview.paid)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">المتبقي:</span><span className="tabular-nums font-bold text-red-700">{formatMoney(preview.remaining)}</span></div>
            </div>
          )}

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm mb-1 block">نوع المطالبة</Label>
              <Select value={claimType} onValueChange={setClaimType}>
                <SelectTrigger data-testid="refer-claim-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="financial">مطالبة مالية</SelectItem>
                  <SelectItem value="eviction">مطالبة إخلاء</SelectItem>
                  <SelectItem value="execution">مطالبة تنفيذ</SelectItem>
                  <SelectItem value="judicial">مطالبة قضائية</SelectItem>
                  <SelectItem value="custom">مطالبة مخصصة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">الأولوية</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="refer-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">منخفضة</SelectItem>
                  <SelectItem value="medium">متوسطة</SelectItem>
                  <SelectItem value="high">عالية</SelectItem>
                  <SelectItem value="urgent">عاجلة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">ملاحظات لفريق القانونية</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="اذكر أي معلومات إضافية تفيد الإدارة القانونية..." data-testid="refer-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>إلغاء</Button>
            <Button className="bg-[#1E3A5F] hover:bg-[#152B48] text-white" onClick={submit} disabled={loading} data-testid="refer-submit">
              {loading ? 'جارٍ الإحالة...' : 'تأكيد الإحالة'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
